import { PropsWithChildren, useEffect, useMemo } from "react";
import {
  FieldValues,
  FormProvider,
  RegisterOptions,
  UseFormReset,
  UseFormSetError,
  useForm
} from "react-hook-form";
import ErrorMessages from "./errors/ErrorMessages";

export type InputBaseParams = {
  name: string;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs"
  >;
  className?: string;
  value?: string;
  errorClassName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onChangeCallback?: (T: string) => void;
};

/**
 * A flexible form manager thats makes
 * use of the custom form components used
 * in the CTB webapp
 *
 * Child elements can be a mix of form elements
 * and controlled input elements.
 *
 * @param defaultValues An object containing the default values of the form inputs.
 * The form will get types from this object so all inputs must be defined
 *
 * @param className The stlying to apply to the form container
 */
const Form = <T extends FieldValues>({
  defaultErrorDisplay = true,
  defaultValues,
  className,
  children,
  onSubmit
}: PropsWithChildren<{
  defaultErrorDisplay?: boolean;
  defaultValues: T;
  className?: string;
  onSubmit: (
    data: T,
    setError: UseFormSetError<T>,
    reset: UseFormReset<T>
  ) => void;
}>) => {
  const form = useForm<T>({ defaultValues: defaultValues as any, shouldUnregister: false });

  // Reset form with default values when they change
  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  return (
    <FormProvider {...form}>
      <form
        className={className}
        onSubmit={form.handleSubmit(data =>
          onSubmit(data, form.setError, form.reset)
        )}
      >
        {defaultErrorDisplay && <ErrorMessages />}

        {children}
      </form>
    </FormProvider>
  );
};

export default Form;
